import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Calendar, Users } from "lucide-react";
import UnifiedSearchBar from "@/components/UnifiedSearchBar";
import { ChipGroup } from "@/components/FilterChips";
import PostProblemModal from "@/components/PostProblemModal";
import { CATEGORIES } from "@/data/mockPeople";
import { OPEN_PROBLEMS, URGENCY } from "@/data/openProblems";
import { api, useAuth, goToLogin } from "@/contexts/AuthContext";
import { realProblemToOpen } from "@/lib/adapters";
import { toast } from "sonner";

const URGENCY_STYLES = {
  Today: "bg-[#FEE2E2] text-[#B91C1C] ring-[#FECACA]",
  "This week": "bg-[#FEF3C7] text-[#B45309] ring-[#FDE68A]",
  "This month": "bg-[#DBEAFE] text-[#1E40AF] ring-[#BFDBFE]",
  "No rush": "bg-[#F3F4F6] text-[#4B5563] ring-[#E5E7EB]",
};

const formatDate = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - d) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff} days ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

function ProblemCard({ problem, onInterested }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(problem.interestedCount);
  const [claimed, setClaimed] = useState(false);

  const handleInterest = async (e) => {
    e.preventDefault();
    if (problem.isDemo) {
      toast.info("This is a demo problem — interest is disabled.");
      return;
    }
    if (!user) {
      goToLogin("/problems");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/open-problems/${problem.id}/interest`);
      setCount(data.interested_count);
      setClaimed(true);
      toast.success("Sent — the poster will get an email.");
      if (onInterested) onInterested(problem.id, data.interested_count);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't register interest.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const PosterInner = (
    <div className="flex items-center gap-2.5">
      {problem.postedBy.photo ? (
        <img
          src={problem.postedBy.photo}
          alt={problem.postedBy.name}
          className="w-8 h-8 rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="w-8 h-8 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF] text-xs font-semibold">
          {(problem.postedBy.name || "?").slice(0, 1).toUpperCase()}
        </span>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-[#111827] truncate">
          {problem.postedBy.name}
        </p>
        <p className="text-xs text-[#9CA3AF] truncate">
          {problem.postedBy.role}
        </p>
      </div>
    </div>
  );

  return (
    <article
      data-testid={`open-problem-${problem.id}`}
      className="relative bg-white rounded-3xl p-6 ring-1 ring-[#F3F4F6] hover:ring-[#B8FFDD] card-lift flex flex-col"
    >
      {/* Whole-card click target for details. Inner interactive elements use z-10 + stopPropagation. */}
      <Link
        to={`/problems/${problem.id}`}
        data-testid={`open-detail-${problem.id}`}
        aria-label={`View details for ${problem.title}`}
        className="absolute inset-0 rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3FFFB6]"
      />

      <div className="relative z-10 flex items-start justify-between gap-3 pointer-events-none">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563]">
          {problem.category}
        </span>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${URGENCY_STYLES[problem.urgency]}`}
        >
          {problem.urgency}
        </span>
      </div>

      <h3 className="relative z-10 mt-4 font-display font-semibold text-lg text-[#111827] leading-snug group-hover:text-[#3FFFB6] pointer-events-none">
        {problem.title}
      </h3>
      <p className="relative z-10 mt-2 text-sm text-[#4B5563] leading-relaxed line-clamp-3 pointer-events-none">
        {problem.description}
      </p>

      <div className="relative z-10 mt-5 pt-5 border-t border-[#F3F4F6] flex items-center justify-between gap-3 pointer-events-none">
        {problem.postedBy.personId ? (
          <Link
            to={`/people/${problem.postedBy.personId}`}
            onClick={(e) => e.stopPropagation()}
            className="pointer-events-auto hover:opacity-80 transition-opacity"
          >
            {PosterInner}
          </Link>
        ) : (
          <div>{PosterInner}</div>
        )}
        <div className="flex flex-col items-end text-xs text-[#9CA3AF] gap-1">
          <span className="inline-flex items-center gap-1">
            <Calendar size={11} /> {formatDate(problem.datePosted)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Users size={11} /> {count} interested
          </span>
        </div>
      </div>

      {problem.isDemo && (
        <p className="relative z-10 mt-3 inline-flex self-start items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#D6FFEB] text-[#3FFFB6] pointer-events-none">
          Demo problem
        </p>
      )}

      {problem.isDemo ? (
        <Link
          to={`/problems/${problem.id}`}
          data-testid={`open-view-${problem.id}`}
          className="relative z-10 mt-5 w-full h-11 rounded-full bg-white ring-1 ring-[#E5E7EB] hover:ring-[#3FFFB6] hover:text-[#3FFFB6] text-[#111827] text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2"
        >
          <BadgeCheck size={16} />
          View demo details
        </Link>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleInterest(e);
          }}
          data-testid={`interested-${problem.id}`}
          disabled={busy || claimed}
          className="relative z-10 mt-5 w-full h-11 rounded-full bg-[#111827] hover:bg-black disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF] disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2"
        >
          <BadgeCheck size={16} />
          {claimed ? "Interest sent" : "I'm Interested"}
        </button>
      )}
    </article>
  );
}

export default function OpenProblemsPage() {
  const [query, setQuery] = useState("");
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeUrgency, setActiveUrgency] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [realProblems, setRealProblems] = useState([]);

  const loadReal = () => {
    api
      .get("/open-problems")
      .then((r) => setRealProblems((r.data || []).map(realProblemToOpen)))
      .catch(() => setRealProblems([]));
  };

  useEffect(() => {
    loadReal();
  }, []);

  const allProblems = useMemo(
    // Real user-posted problems only — no demo problems anywhere in the app.
    () => [...realProblems],
    [realProblems]
  );

  // Merge predefined list with any custom categories users have posted so
  // they become clickable filter chips.
  const mergedCategories = useMemo(() => {
    const set = new Set(CATEGORIES);
    for (const p of allProblems) if (p.category) set.add(p.category);
    return [...set];
  }, [allProblems]);

  const toggle = (list, setList) => (item) =>
    setList(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const filtered = useMemo(() => {
    return allProblems.filter((p) => {
      if (activeCategories.length && !activeCategories.includes(p.category))
        return false;
      if (activeUrgency.length && !activeUrgency.includes(p.urgency))
        return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = [
          p.title,
          p.description,
          p.category,
          p.postedBy.name,
          p.postedBy.role,
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [query, activeCategories, activeUrgency, allProblems]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <h1 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#111827]">
            AI questions waiting to be solved.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#4B5563] leading-relaxed">
            Browse real problems posted by people who need help. If one matches
            your expertise, submit your answer. The OP will select the best
            answer, and your profile will show you solved this for them.
          </p>
        </div>
        <button
          onClick={() => setPostOpen(true)}
          data-testid="post-problem-cta"
          className="self-start md:self-auto px-6 h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold transition-colors"
        >
          Post a problem
        </button>
      </div>

      <div className="mt-10">
        <UnifiedSearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search open problems…"
        />
        <div className="mt-6 space-y-4">
          <ChipGroup
            label="Category"
            options={mergedCategories}
            active={activeCategories}
            onToggle={toggle(activeCategories, setActiveCategories)}
            testIdPrefix="op-filter-category"
            onSubmit={(v) => {
              if (!activeCategories.includes(v)) setActiveCategories([...activeCategories, v]);
            }}
            submitLabel="Submit a category"
          />
          <ChipGroup
            label="Urgency"
            options={URGENCY}
            active={activeUrgency}
            onToggle={toggle(activeUrgency, setActiveUrgency)}
            testIdPrefix="op-filter-urgency"
          />
        </div>
      </div>

      <div className="mt-12 flex items-baseline justify-between">
        <h2 className="font-display font-semibold text-lg text-[#111827]">
          {filtered.length} open{" "}
          {filtered.length === 1 ? "problem" : "problems"}
        </h2>
        <p className="text-sm text-[#9CA3AF]">Newest first</p>
      </div>

      <section
        data-testid="open-problems-grid"
        className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filtered.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <p className="text-[#4B5563]">No open problems match those filters.</p>
            <button
              onClick={() => {
                setQuery("");
                setActiveCategories([]);
                setActiveUrgency([]);
              }}
              className="mt-4 text-[#3FFFB6] font-medium text-sm"
            >
              Clear filters
            </button>
          </div>
        ) : (
          filtered.map((p) => <ProblemCard key={p.id} problem={p} />)
        )}
      </section>

      <PostProblemModal
        open={postOpen}
        onOpenChange={setPostOpen}
        onCreated={loadReal}
      />
    </div>
  );
}
